import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Empty, Flex, Skeleton, Typography } from 'antd';
import { useIntl } from 'react-intl';
import moment from 'moment';
import TransactionsList from './TransactionsList';
import { useMessage } from 'shared/libs/hooks/useMessage/useMessage';
import { Transaction, TransactionDetails } from 'widgets/transactions/model/types';
import { getErrorDefaultMessage } from 'shared/libs/helpers';
import Result from 'shared/ui/Result';
import TransactionDetailsModal from './TransactionDetails';
import { groupTransactions } from '../model/utils';
import { ALL_CONFIG } from '../model/constants';
import {
  useLazyGetTransactionByIdQuery,
  useLazyGetTransactionsByEmiQuery
} from '../api';
import { GetTransactionsParams } from '../api/dto';
import Filters from '../components/Filters';
import { MonthSpendingAllAccounts } from '../components/MonthSpendingAllAccounts';
import { EMI } from 'shared/model/types';
import { TransactionFilters } from '../model/types';
import { useSelector } from 'react-redux';
import { getCurrentEmi } from 'shared/config/store/selectors/app';

type TransactionsViewByEmiProps = {
  emi?: EMI;
  showTitle?: boolean;
};

const TransactionsViewByEmi: React.FC<TransactionsViewByEmiProps> = ({ emi, showTitle = true }) => {
  const { formatMessage: t } = useIntl();
  const currentEmi = useSelector(getCurrentEmi);
  const emiToUse = emi || currentEmi;
  const currentMonthStart = moment().startOf('month').format('DD-MM-YYYY');
  const [fetchTransactions, { data, error, isError, isLoading, isFetching, isSuccess }] =
    useLazyGetTransactionsByEmiQuery();
  const config = useRef<GetTransactionsParams>(ALL_CONFIG);
  const [canLoadMore, setCanLoadMore] = useState<boolean>(true);
  const [activeTransaction, setActiveTransaction] = useState<Transaction>();
  const [activeTransactionDetails, setActiveTransactionDetails] = useState<TransactionDetails>();
  const { showError } = useMessage();
  const [getTransactionById] = useLazyGetTransactionByIdQuery();
  const [transactionLoading, setTransactionLoading] = useState(false);

  const onFetchTransactions = () => {
    const currentPage = config.current.page;
    return fetchTransactions({
      emi: emiToUse,
      params: config.current,
      merge: currentPage !== 0
    })
      .unwrap()
      .then(({ response }) => {
        if (currentPage >= Number(response.totalPages) - 1) {
          setCanLoadMore(false);
        } else {
          setCanLoadMore(true);
        }

        config.current = {
          ...config.current,
          page: currentPage + 1
        };
      });
  };

  const handleDateRangeChange = useCallback((startDate: string, endDate: string | undefined) => {
    setCanLoadMore(true);
    config.current = {
      ...config.current,
      start: startDate,
      end: endDate || moment().endOf('day').format('DD-MM-YYYY'),
      page: 0
    };
    onFetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emiToUse]);

  useEffect(() => {
    onFetchTransactions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const renderFilters = () => {
    const standardFilters = [TransactionFilters.IN, TransactionFilters.OUT, TransactionFilters.PENDING];
    return (
      <Filters
        filters={standardFilters}
        onSelect={(type) => {
          const newType = type === 'All' || !type ? undefined : type;
          setCanLoadMore(true);
          config.current = {
            ...config.current,
            filter: newType,
            page: 0
          };

          onFetchTransactions();
        }}
      />
    );
  };

  const onClickTransaction = (transaction: Transaction) => {
    setActiveTransaction(transaction);
    setTransactionLoading(true);
    const accountId = transaction.accountId;
    if (!accountId) {
      showError(t({ id: 'common.message.error' }));
      setTransactionLoading(false);
      return;
    }
    getTransactionById({
      accountId,
      transactionId: transaction.id
    })
      .unwrap()
      .then((response) => {
        setActiveTransactionDetails(response.response);
      })
      .catch(() => {
        showError(t({ id: 'common.message.error' }));
      })
      .finally(() => {
        setTransactionLoading(false);
      });
  };

  const renderList = () => {
    if (isError) {
      return (
        <Flex vertical align='center' gap={16} style={{ width: '100%', marginTop: 32 }}>
          <Result
            type='error'
            title={t({ id: 'drawer.transactions.wrong' })}
            subtitle={getErrorDefaultMessage(error)}
          />
          <Button
            onClick={() => onFetchTransactions()}
            type='primary'
            style={{ backgroundColor: 'black' }}
          >
            {t({ id: 'drawer.transactions.refresh' })}
          </Button>
        </Flex>
      );
    }

    if (isLoading) {
      return (
        <Flex gap={16} vertical style={{ width: '100%' }}>
          {showTitle && (
            <Typography.Title level={3} style={{ marginBottom: 16 }}>
              {t({ id: 'transactions.title' })}
            </Typography.Title>
          )}
          <Flex>{renderFilters()}</Flex>
          <MonthSpendingAllAccounts 
            emi={emiToUse} 
            start={currentMonthStart}
            onDateRangeChange={handleDateRangeChange}
          />
          <Skeleton active paragraph={{ rows: 5 }} />
        </Flex>
      );
    }

    if (isSuccess) {
      if (!data) return null;
      const transactionsList = data.response.transactions;
      const groupedData = groupTransactions(transactionsList);

      return (
        <Flex vertical style={{ width: '100%' }}>
          {showTitle && (
            <Typography.Title level={3} style={{ marginBottom: 16 }}>
              {t({ id: 'transactions.title' })}
            </Typography.Title>
          )}
          <Flex gap={16} vertical>
            <Flex>{renderFilters()}</Flex>
            <MonthSpendingAllAccounts 
              emi={emiToUse} 
              start={currentMonthStart}
              onDateRangeChange={handleDateRangeChange}
            />
            {transactionsList.length ? (
              <>
                <TransactionsList
                  transactions={groupedData}
                  onClickTransaction={onClickTransaction}
                />
                {isFetching && (
                  <div style={{ paddingInline: 16, paddingTop: 16 }}>
                    <Skeleton active paragraph={{ rows: 3 }} />
                  </div>
                )}
                {canLoadMore && !isFetching && (
                  <div style={{ paddingInline: 16 }}>
                    <Button
                      style={{ marginBottom: 32, backgroundColor: 'black', color: 'white' }}
                      disabled={isFetching}
                      onClick={() => onFetchTransactions()}
                    >
                      {t({ id: 'buttons.loadMore' })}
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Flex vertical align='center' style={{ width: '100%' }}>
                <Empty description={t({ id: 'drawer.transactions.notFound' })} />
              </Flex>
            )}
          </Flex>
        </Flex>
      );
    }

    return null;
  };

  return (
    <>
      <Flex style={{ marginBottom: 32 }} justify='space-between'>
        {renderList()}
      </Flex>
      <TransactionDetailsModal
        transaction={activeTransaction}
        transactionDetails={activeTransactionDetails}
        onClose={() => setActiveTransaction(undefined)}
        loading={transactionLoading}
      />
    </>
  );
};

export default TransactionsViewByEmi;

